import { createStore } from 'vuex';
import api from '../services/api';
import { disconnectSocket, emitOptimisticOperation, subscribeScopes, ensureSocketHealthy, hardReconnectSocket } from '../services/socket';
import { publishLocalChange, subscribeLocalChanges } from '../services/channel';
import {
  applyIncomingOperation,
  applyListLocal,
  applyTaskLocal,
  bootstrapRemote,
  ensureGuestInbox,
  flushPending,
  getDeviceMeta,
  loadLocalState,
  pullRemote,
  queueOperation,
  removeListLocal,
  removeTaskLocal,
  resetLocalForLogout,
  isTaskCompleted,
} from '../services/sync-engine';
import { db } from '../services/idb';
import { NOTE_COLORS, normalizeNoteColor } from '../constants/note-colors';

let syncRetryTimer = null;
let syncRetryDelay = 0;
let sessionInvalidationPromise = null;
let foregroundSyncInterval = null;
const pendingOpTimers = new Map();

function buildDefaultBilling() {
  return {
    configured: false,
    ad_free_active: false,
    ads_enabled: true,
    ads_disabled_until: null,
    plan: null,
    status: null,
    price_label: '$50/anual',
    plans: [],
    limits: { max_groups: 1, max_members_per_group: 4 },
    loaded: false,
  };
}

function newUuid() {
  return globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function isQuickSheetList(list) {
  return !!list && (String(list.id || '') === 'guest-inbox' || Number(list.is_quick_sheet || 0) === 1);
}

function isVisibleListForMode(list, mode) {
  if (!list || list.deleted_at) return false;
  if (mode === 'user') return String(list.id || '') !== 'guest-inbox';
  return true;
}

function sortListsForUi(lists = []) {
  return [...lists].sort((a, b) => {
    const quickDiff = Number(b?.is_quick_sheet || 0) - Number(a?.is_quick_sheet || 0);
    if (quickDiff !== 0) return quickDiff;
    const orderDiff = Number(a?.sort_order || 0) - Number(b?.sort_order || 0);
    if (orderDiff !== 0) return orderDiff;
    return String(a?.created_at || '').localeCompare(String(b?.created_at || ''));
  });
}

function getVisibleListsForMode(lists = [], mode = 'guest') {
  return sortListsForUi(lists.filter((item) => isVisibleListForMode(item, mode)));
}

function nextListSortOrder(lists = [], { groupId = null, mode = 'user' } = {}) {
  return lists.filter((item) => {
    if (!isVisibleListForMode(item, mode)) return false;
    if (isQuickSheetList(item)) return false;
    return groupId ? item.group_id === groupId : !item.group_id;
  }).length;
}

function findUserQuickList(lists = []) {
  return sortListsForUi(
    lists.filter((item) => !item.deleted_at && Number(item.is_quick_sheet || 0) === 1)
  )[0] || null;
}


const store = createStore({
  state() {
    return {
      mode: localStorage.getItem('notas_access_token') ? 'user' : 'guest',
      user: null,
      lists: [],
      tasks: [],
      groups: [],
      memberships: [],
      selectedListId: localStorage.getItem('notas_selected_list_id') || 'guest-inbox',
      online: navigator.onLine,
      syncing: false,
      socketConnected: false,
      authModalOpen: false,
      groupModalOpen: false,
      inviteToken: null,
      billing: buildDefaultBilling(),
      saveStatus: localStorage.getItem('notas_access_token') ? 'Sincronizando…' : 'Guardado local',
      pendingOps: {},
    };
  },
  mutations: {
    setMode(state, value) { state.mode = value; },
    setUser(state, value) { state.user = value; },
    setLists(state, value) { state.lists = value; },
    setTasks(state, value) { state.tasks = value; },
    setGroups(state, value) { state.groups = value; },
    setMemberships(state, value) { state.memberships = value; },
    setSelectedListId(state, value) {
      state.selectedListId = value;
      try {
        if (value) {
          localStorage.setItem('notas_selected_list_id', value);
        } else {
          localStorage.removeItem('notas_selected_list_id');
        }
      } catch (error) {
        console.warn(error);
      }
    },
    setOnline(state, value) { state.online = value; },
    setSyncing(state, value) { state.syncing = value; },
    setSocketConnected(state, value) { state.socketConnected = value; },
    setAuthModalOpen(state, value) { state.authModalOpen = value; },
    setGroupModalOpen(state, value) { state.groupModalOpen = value; },
    setInviteToken(state, value) { state.inviteToken = value; },
    setBilling(state, value) { state.billing = { ...buildDefaultBilling(), ...(value || {}), loaded: value?.loaded ?? true }; },
    setSaveStatus(state, value) { state.saveStatus = value; },
    setPendingOp(state, value) {
      const opId = String(value?.op_id || '').trim();
      if (!opId) return;
      state.pendingOps = {
        ...state.pendingOps,
        [opId]: {
          op_id: opId,
          entity_id: String(value?.entity_id || ''),
          entity_type: String(value?.entity_type || ''),
          action: String(value?.action || ''),
          expires_at: Number(value?.expires_at || (Date.now() + 12000)),
        },
      };
    },
    removePendingOp(state, opId) {
      const normalized = String(opId || '').trim();
      if (!normalized || !state.pendingOps[normalized]) return;
      const next = { ...state.pendingOps };
      delete next[normalized];
      state.pendingOps = next;
    },
    clearPendingOps(state) {
      state.pendingOps = {};
    },
  },
  getters: {
    visibleLists(state) {
      return getVisibleListsForMode(state.lists, state.mode);
    },
    currentList(state, getters) {
      return getters.visibleLists.find((item) => item.id === state.selectedListId)
        || getters.visibleLists[0]
        || null;
    },
    currentTasks(state, getters) {
      const currentListId = getters.currentList?.id || state.selectedListId;
      return [...state.tasks]
        .filter((item) => !item.deleted_at && item.list_id === currentListId)
        .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));
    },
    pendingTaskIds(state) {
      const now = Date.now();
      return [...new Set(Object.values(state.pendingOps || {})
        .filter((item) => item?.entity_type === 'task' && item?.entity_id && Number(item?.expires_at || 0) > now)
        .map((item) => item.entity_id))];
    },
  },
  actions: {
    async initApp({ state, dispatch, commit }) {
      if (!window.__notasLocalSyncSubscribed) {
        window.__notasLocalSyncSubscribed = true;
        subscribeLocalChanges(async () => {
          await dispatch('hydrateLocal');
          if (localStorage.getItem('notas_access_token')) {
            await dispatch('pullRemote');
          }
        });
      }

      if (!window.__notasAuthInvalidSubscribed) {
        window.__notasAuthInvalidSubscribed = true;
        window.addEventListener('notas:auth-invalid', async () => {
          await dispatch('handleSessionInvalidation');
        });
      }

      if (!window.__notasForegroundSyncSubscribed) {
        window.__notasForegroundSyncSubscribed = true;

        const syncOnForeground = async ({ forceReconnect = false, reason = 'foreground' } = {}) => {
          if (document.visibilityState && document.visibilityState !== 'visible') return;
          commit('setOnline', navigator.onLine);
          if (!localStorage.getItem('notas_access_token')) return;
          try {
            await ensureSocketHealthy({ forceReconnect, reason });
            await dispatch('syncNow');
          } catch (error) {
            console.warn(error);
          }
        };

        document.addEventListener('visibilitychange', () => {
          if (!document.hidden) {
            syncOnForeground({ reason: 'visibilitychange' });
          }
        });

        window.addEventListener('focus', () => syncOnForeground({ reason: 'focus' }));
        window.addEventListener('pageshow', (event) => syncOnForeground({
          forceReconnect: !!event.persisted,
          reason: event.persisted ? 'pageshow-bfcache' : 'pageshow',
        }));
        window.addEventListener('pagehide', () => {
          disconnectSocket({ preserveScopes: true, silent: true });
        });

        if (!foregroundSyncInterval) {
          foregroundSyncInterval = window.setInterval(() => {
            if (document.hidden || !navigator.onLine || !localStorage.getItem('notas_access_token')) return;
            ensureSocketHealthy({ reason: 'interval-health' }).catch((error) => console.warn(error));
            dispatch('pullRemote').catch((error) => console.warn(error));
          }, 10000);
        }
      }

      window.addEventListener('online', async () => {
        commit('setOnline', true);
        commit('setSaveStatus', state.mode === 'user' ? 'Reconectado. Sincronizando…' : 'Guardado local');
        if (localStorage.getItem('notas_access_token')) {
          await hardReconnectSocket('browser-online');
        }
        await dispatch('syncNow');
      });

      window.addEventListener('offline', () => {
        commit('setOnline', false);
        commit('setSaveStatus', state.mode === 'user' ? 'Sin conexión. Reintentaremos…' : 'Guardado local');
      });

      await ensureGuestInbox();
      await dispatch('hydrateLocal');

      if (localStorage.getItem('notas_access_token')) {
        await ensureSocketHealthy({ forceReconnect: true, reason: 'init-app' });
        await dispatch('bootstrap');
      } else {
        commit('setMode', 'guest');
        commit('setUser', null);
        commit('setGroups', []);
        commit('setMemberships', []);
        commit('setBilling', { ...buildDefaultBilling(), loaded: true });
      }

      await dispatch('acceptInviteIfNeeded');
    },

    async hydrateLocal({ commit, state }) {
      const { lists, tasks } = await loadLocalState();
      commit('setLists', lists);
      commit('setTasks', tasks);

      const visibleLists = getVisibleListsForMode(lists, state.mode);
      const exists = visibleLists.some((item) => item.id === state.selectedListId);
      if (!exists) {
        commit('setSelectedListId', visibleLists[0]?.id || 'guest-inbox');
      }
    },

    async markPendingOperation({ commit }, operation) {
      const opId = String(operation?.op_id || '').trim();
      if (!opId) return;

      commit('setPendingOp', {
        op_id: opId,
        entity_id: operation?.entity_id,
        entity_type: operation?.entity_type,
        action: operation?.action,
        expires_at: Date.now() + 12000,
      });

      if (pendingOpTimers.has(opId)) {
        clearTimeout(pendingOpTimers.get(opId));
      }

      pendingOpTimers.set(opId, window.setTimeout(() => {
        pendingOpTimers.delete(opId);
        commit('removePendingOp', opId);
      }, 12000));
    },

    async confirmPendingOperation({ commit }, opId) {
      const normalized = String(opId || '').trim();
      if (!normalized) return;
      if (pendingOpTimers.has(normalized)) {
        clearTimeout(pendingOpTimers.get(normalized));
        pendingOpTimers.delete(normalized);
      }
      commit('removePendingOp', normalized);
    },

    async confirmPendingFromServerEvent({ state, dispatch }, payload = {}) {
      const targetOpId = String(payload?.op_id || '').trim();
      if (targetOpId) {
        await dispatch('confirmPendingOperation', targetOpId);
      }

      const entityId = String(payload?.entity_id || '').trim();
      const entityType = String(payload?.entity_type || '').trim();
      if (!entityId || !entityType) return;

      const matches = Object.values(state.pendingOps || {})
        .filter((item) => item?.entity_id === entityId && item?.entity_type === entityType)
        .map((item) => item.op_id);

      for (const opId of matches) {
        await dispatch('confirmPendingOperation', opId);
      }
    },

    async relayOptimisticOperation({ state, dispatch }, operation) {
      if (state.mode !== 'user' || !operation?.op_id) return;
      await dispatch('markPendingOperation', operation);
      const delivered = await emitOptimisticOperation({
        ...operation,
        actor_user_id: state.user?.id || 0,
      });
      if (!delivered) {
        await ensureSocketHealthy({ forceReconnect: true, reason: 'relay-fallback' }).catch(() => null);
      }
    },

    async applyRealtimeRelay({ state, dispatch }, operation) {
      if (!operation?.op_id) return;

      await applyIncomingOperation({
        ...operation,
        actor_user_id: operation.actor_user_id || 0,
      });

      await dispatch('markPendingOperation', operation);
      await dispatch('hydrateLocal');
      await dispatch('broadcastLocalRefresh');
    },

    async bootstrap({ state, commit, dispatch }) {
      commit('setSyncing', true);
      try {
        const data = await bootstrapRemote();

        if (data.mode === 'user') {
          commit('setMode', 'user');
          commit('setUser', data.user);
          commit('setGroups', data.groups || []);
          commit('setMemberships', data.memberships || []);
          await dispatch('loadBillingStatus');
          commit('setSaveStatus', state.online ? 'Guardado' : 'Sin conexión. Reintentaremos…');
        } else {
          if (localStorage.getItem('notas_access_token')) {
            await dispatch('handleSessionInvalidation');
            return;
          }

          commit('setMode', 'guest');
          commit('setUser', null);
          commit('setGroups', []);
          commit('setMemberships', []);
          commit('setBilling', { ...buildDefaultBilling(), loaded: true });
          commit('setSaveStatus', 'Guardado local');
        commit('clearPendingOps');
        }

        await dispatch('hydrateLocal');

        subscribeScopes({
          lists: (data.lists || []).map((item) => item.id),
          groups: (data.groups || []).map((item) => item.id),
        });
      } finally {
        commit('setSyncing', false);
      }
    },

    async clearGuestSyncResidue({ state, commit }) {
      if (state.mode !== 'user') return;

      const guestTasks = await db.tasks.where('list_id').equals('guest-inbox').toArray();
      const guestTaskIds = new Set(guestTasks.map((task) => task.id));
      const operations = await db.operations.toArray();

      const staleOperations = operations.filter((op) => {
        if ((op.status || 'pending') !== 'pending') return false;
        if (op.entity_id === 'guest-inbox') return true;
        if (guestTaskIds.has(op.entity_id)) return true;
        if (op.payload?.list_id === 'guest-inbox') return true;
        return false;
      });

      if (!staleOperations.length) return;

      await Promise.all(staleOperations.map((op) => db.operations.delete(op.op_id)));
      commit('setSaveStatus', 'Guardado pendiente. Limpiando cola antigua…');
    },

    async handleSessionInvalidation({ commit, dispatch }) {
      if (sessionInvalidationPromise) {
        return sessionInvalidationPromise;
      }

      sessionInvalidationPromise = (async () => {
        localStorage.removeItem('notas_access_token');
        localStorage.removeItem('notas_refresh_token');
        localStorage.removeItem('notas_device_id');

        disconnectSocket();
        await resetLocalForLogout();

        if (syncRetryTimer) {
          clearTimeout(syncRetryTimer);
          syncRetryTimer = null;
        }
        syncRetryDelay = 0;

        commit('setMode', 'guest');
        commit('setUser', null);
        commit('setGroups', []);
        commit('setMemberships', []);
        commit('setBilling', { ...buildDefaultBilling(), loaded: true });
        commit('setAuthModalOpen', false);
        commit('setSaveStatus', 'Guardado local');
        commit('clearPendingOps');

        await dispatch('hydrateLocal');
        commit('setSelectedListId', 'guest-inbox');
        await dispatch('broadcastLocalRefresh');
      })();

      try {
        await sessionInvalidationPromise;
      } finally {
        sessionInvalidationPromise = null;
      }
    },

    async syncNow({ state, commit, dispatch }) {
      if (state.mode !== 'user') {
        commit('setSaveStatus', 'Guardado local');
        return;
      }

      if (!state.online) {
        commit('setSaveStatus', 'Sin conexión. Reintentaremos…');
        await dispatch('scheduleSyncRetry');
        return;
      }

      commit('setSyncing', true);
      commit('setSaveStatus', 'Guardando…');

      try {
        await dispatch('clearGuestSyncResidue');
        const flushResult = await flushPending();
        await pullRemote();
        await dispatch('hydrateLocal');

        for (const result of flushResult?.results || []) {
          await dispatch('confirmPendingOperation', result?.op_id);
        }

        if (syncRetryTimer) {
          clearTimeout(syncRetryTimer);
          syncRetryTimer = null;
        }
        syncRetryDelay = 0;
        commit('setSaveStatus', 'Guardado');
      } catch (error) {
        console.warn(error);
        commit('setSaveStatus', 'Guardado pendiente. Reintentando…');
        await dispatch('scheduleSyncRetry');
      } finally {
        commit('setSyncing', false);
      }
    },

    async scheduleSyncRetry({ state, dispatch }) {
      if (state.mode !== 'user') return;
      if (syncRetryTimer) return;

      syncRetryDelay = Math.min(syncRetryDelay ? syncRetryDelay * 2 : 3000, 30000);
      syncRetryTimer = window.setTimeout(async () => {
        syncRetryTimer = null;
        await dispatch('syncNow');
      }, syncRetryDelay);
    },

    async pullRemote({ dispatch }) {
      await pullRemote();
      await dispatch('hydrateLocal');
    },

    async login({ commit, dispatch }, credentials) {
      const response = await api.post('/auth/login', {
        ...credentials,
        ...getDeviceMeta(),
      });

      const data = response.data?.data;
      localStorage.setItem('notas_access_token', data.access_token);
      localStorage.setItem('notas_refresh_token', data.refresh_token);
      localStorage.setItem('notas_device_id', data.device_id);

      commit('setMode', 'user');
      commit('setUser', data.user);
      commit('setAuthModalOpen', false);

      await dispatch('clearGuestSyncResidue');

      await ensureSocketHealthy({ forceReconnect: true, reason: 'login' });
      await dispatch('bootstrap');
      await dispatch('importGuestSheetIfNeeded');
      await dispatch('syncNow');
      await dispatch('tryAcceptPendingInvite');
      await dispatch('broadcastLocalRefresh');
    },

    async register({ commit, dispatch }, payload) {
      const response = await api.post('/auth/register', {
        ...payload,
        ...getDeviceMeta(),
      });

      const data = response.data?.data;
      localStorage.setItem('notas_access_token', data.access_token);
      localStorage.setItem('notas_refresh_token', data.refresh_token);
      localStorage.setItem('notas_device_id', data.device_id);

      commit('setMode', 'user');
      commit('setUser', data.user);
      commit('setAuthModalOpen', false);

      await dispatch('clearGuestSyncResidue');

      await ensureSocketHealthy({ forceReconnect: true, reason: 'register' });
      await dispatch('bootstrap');
      await dispatch('importGuestSheetIfNeeded');
      await dispatch('syncNow');
      await dispatch('tryAcceptPendingInvite');
      await dispatch('broadcastLocalRefresh');
    },

    async logout({ dispatch }) {
      const refreshToken = localStorage.getItem('notas_refresh_token');
      const deviceId = localStorage.getItem('notas_device_id');

      if (refreshToken && deviceId) {
        try {
          await api.post('/auth/logout', { refresh_token: refreshToken, device_id: deviceId });
        } catch (error) {
          console.warn(error);
        }
      }

      await dispatch('handleSessionInvalidation');
    },

    async importGuestSheetIfNeeded({ state, commit, dispatch }) {
      const guestList = state.lists.find((item) => item.id === 'guest-inbox' && !item.deleted_at);
      const guestTasks = state.tasks.filter((item) => item.list_id === 'guest-inbox' && !item.deleted_at);
      if (!guestList || !guestTasks.length) return;

      const quickList = findUserQuickList(state.lists);
      if (!quickList) return;

      const existingQuickTasks = state.tasks.filter((item) => item.list_id === quickList.id && !item.deleted_at).length;

      for (let index = 0; index < guestTasks.length; index += 1) {
        const task = guestTasks[index];
        const taskId = newUuid();
        const op = await queueOperation('create_task', 'task', taskId, {
          list_id: quickList.id,
          body: task.body,
          sort_order: existingQuickTasks + index,
        }, 'personal', 'personal');

        await applyIncomingOperation({
          ...op,
          actor_user_id: state.user?.id || 0,
          entity_id: taskId,
          payload: { list_id: quickList.id, body: task.body, sort_order: existingQuickTasks + index },
        });
      }

      await Promise.all(guestTasks.map((task) => db.tasks.delete(task.id)));
      commit('setSelectedListId', quickList.id);
      await dispatch('hydrateLocal');
    },

    async saveQuickSheetAsList({ state, commit, dispatch }, { title, groupId = null }) {
      if (state.mode !== 'user') {
        commit('setAuthModalOpen', true);
        return null;
      }

      const quickList = findUserQuickList(state.lists);
      const quickTasks = quickList
        ? state.tasks.filter((item) => item.list_id === quickList.id && !item.deleted_at)
        : [];
      if (!quickList || !quickTasks.length) return null;

      const userId = Number(state.user?.id || 0);
      const targetScopeType = groupId ? 'group' : 'personal';
      const targetScopeId = groupId || 'personal';
      const authorName = state.user?.nombre || state.user?.username || 'Tú';
      const targetSortOrder = nextListSortOrder(state.lists, { groupId, mode: state.mode });
      const normalizedQuickColor = normalizeNoteColor(quickList.color || NOTE_COLORS[0]);

      const promoteOp = await queueOperation('move_list', 'list', quickList.id, {
        target_scope_type: targetScopeType,
        target_scope_id: targetScopeId,
        group_id: groupId || null,
        owner_user_id: groupId ? null : userId,
        sort_order: targetSortOrder,
        title,
        color: normalizedQuickColor,
        is_quick_sheet: 0,
      }, 'personal', 'personal');

      await applyIncomingOperation({
        ...promoteOp,
        actor_user_id: userId,
      });

      await applyListLocal({
        ...quickList,
        id: quickList.id,
        owner_user_id: groupId ? null : userId,
        group_id: groupId || null,
        title,
        color: normalizedQuickColor,
        sort_order: targetSortOrder,
        is_quick_sheet: 0,
        body_updated_at: promoteOp.client_ts,
        updated_at: promoteOp.client_ts,
        last_activity_at: promoteOp.client_ts,
      });

      const newQuickListId = newUuid();
      const createQuickOp = await queueOperation('create_list', 'list', newQuickListId, {
        title: 'Hoja rápida',
        color: normalizedQuickColor,
        sort_order: 0,
        author_name: authorName,
        is_quick_sheet: 1,
      }, 'personal', 'personal');

      await applyIncomingOperation({
        ...createQuickOp,
        actor_user_id: userId,
        payload: { ...createQuickOp.payload, author_name: authorName, is_quick_sheet: 1 },
      });

      await applyListLocal({
        id: newQuickListId,
        owner_user_id: userId,
        author_user_id: userId,
        author_name: authorName,
        group_id: null,
        is_quick_sheet: 1,
        title: 'Hoja rápida',
        color: normalizedQuickColor,
        sort_order: 0,
        body_updated_at: createQuickOp.client_ts,
        updated_at: createQuickOp.client_ts,
        created_at: createQuickOp.client_ts,
        archived_at: null,
        deleted_at: null,
        last_activity_at: createQuickOp.client_ts,
      });

      commit('setSelectedListId', quickList.id);
      await dispatch('hydrateLocal');
      commit('setSelectedListId', quickList.id);

      await dispatch('relayOptimisticOperation', { ...promoteOp, actor_user_id: userId });
      await dispatch('relayOptimisticOperation', { ...createQuickOp, actor_user_id: userId });

      await dispatch('syncNow');
      await dispatch('hydrateLocal');
      commit('setSelectedListId', quickList.id);
      await dispatch('broadcastLocalRefresh');
      return quickList.id;
    },

    selectList({ commit }, listId) {
      commit('setSelectedListId', listId);
    },

    async createList({ state, commit, dispatch }, { title, groupId = null, color = null }) {
      if (state.mode === 'guest' && state.lists.filter((item) => !item.deleted_at).length >= 1) {
        commit('setAuthModalOpen', true);
        return;
      }

      const listId = newUuid();
      const scopeType = groupId ? 'group' : 'personal';
      const scopeId = groupId || 'personal';
      const payload = {
        title,
        color: normalizeNoteColor(color || NOTE_COLORS[state.lists.filter((item) => !item.deleted_at).length % NOTE_COLORS.length]),
        sort_order: nextListSortOrder(state.lists, { groupId, mode: state.mode }),
        author_name: state.user?.nombre || state.user?.username || 'Tú',
      };

      const op = await queueOperation('create_list', 'list', listId, payload, scopeType, scopeId);
      await applyIncomingOperation({ ...op, actor_user_id: state.user?.id || 0 });
      await dispatch('relayOptimisticOperation', { ...op, actor_user_id: state.user?.id || 0 });

      await dispatch('hydrateLocal');
      commit('setSelectedListId', listId);
      await dispatch('syncNow');
      await dispatch('broadcastLocalRefresh');
    },

    async renameList({ state, dispatch }, { listId, title }) {
      const list = state.lists.find((item) => item.id === listId);
      if (!list) return;

      const scopeType = list.group_id ? 'group' : 'personal';
      const scopeId = list.group_id || 'personal';
      const op = await queueOperation('update_list', 'list', listId, {
        title,
        sort_order: list.sort_order,
        color: normalizeNoteColor(list.color),
      }, scopeType, scopeId);

      await applyIncomingOperation({ ...op, actor_user_id: state.user?.id || 0 });
      await dispatch('relayOptimisticOperation', { ...op, actor_user_id: state.user?.id || 0 });
      await dispatch('hydrateLocal');
      await dispatch('syncNow');
      await dispatch('broadcastLocalRefresh');
    },

    async moveListToScope({ state, dispatch }, { listId, targetGroupId = null }) {
      const list = state.lists.find((item) => item.id === listId && !item.deleted_at);
      if (!list || list.id === 'guest-inbox') return;
      if (list.group_id && Number(list.author_user_id || 0) !== Number(state.user?.id || 0)) {
        throw new Error('Solo el autor puede mover una hoja compartida.');
      }

      const sourceScopeType = list.group_id ? 'group' : 'personal';
      const sourceScopeId = list.group_id || 'personal';
      const targetScopeType = targetGroupId ? 'group' : 'personal';
      const targetScopeId = targetGroupId || 'personal';

      if (sourceScopeType === targetScopeType && sourceScopeId === targetScopeId) {
        return;
      }

      const sortOrder = nextListSortOrder(state.lists, { groupId: targetGroupId, mode: state.mode });
      const op = await queueOperation('move_list', 'list', listId, {
        target_scope_type: targetScopeType,
        target_scope_id: targetScopeId,
        group_id: targetGroupId || null,
        owner_user_id: targetGroupId ? null : Number(state.user?.id || 0),
        sort_order: sortOrder,
        title: list.title,
        color: normalizeNoteColor(list.color),
        is_quick_sheet: Number(list.is_quick_sheet || 0),
      }, sourceScopeType, sourceScopeId);

      await applyIncomingOperation({ ...op, actor_user_id: state.user?.id || 0 });
      await dispatch('relayOptimisticOperation', { ...op, actor_user_id: state.user?.id || 0 });
      await dispatch('hydrateLocal');
      await dispatch('syncNow');
      await dispatch('broadcastLocalRefresh');
    },

    async deleteList({ state, commit, dispatch }, listId) {
      const list = state.lists.find((item) => item.id === listId);
      if (!list) return;
      if (list.group_id && Number(list.author_user_id || 0) !== Number(state.user?.id || 0)) {
        throw new Error('Solo el autor puede eliminar una hoja compartida.');
      }

      const scopeType = list.group_id ? 'group' : 'personal';
      const scopeId = list.group_id || 'personal';
      const op = await queueOperation('delete_list', 'list', listId, {}, scopeType, scopeId);
      await removeListLocal(listId, op.client_ts);
      await dispatch('relayOptimisticOperation', { ...op, actor_user_id: state.user?.id || 0 });
      await dispatch('hydrateLocal');

      const nextList = getVisibleListsForMode(
        state.lists.filter((item) => item.id !== listId),
        state.mode
      )[0];
      commit('setSelectedListId', nextList?.id || 'guest-inbox');
      await dispatch('syncNow');
      await dispatch('broadcastLocalRefresh');
    },

    async updateListColor({ state, dispatch }, { listId, color }) {
      const list = state.lists.find((item) => item.id === listId);
      if (!list) return;
      const op = await queueOperation('update_list', 'list', listId, {
        title: list.title,
        sort_order: list.sort_order,
        color: normalizeNoteColor(color),
      }, list.group_id ? 'group' : 'personal', list.group_id || 'personal');

      await applyIncomingOperation({ ...op, actor_user_id: state.user?.id || 0 });
      await dispatch('relayOptimisticOperation', { ...op, actor_user_id: state.user?.id || 0 });
      await dispatch('hydrateLocal');
      await dispatch('syncNow');
      await dispatch('broadcastLocalRefresh');
    },

    async addTask({ state, dispatch }, body) {
      const list = state.lists.find((item) => item.id === state.selectedListId);
      if (!list) return null;

      const taskId = newUuid();
      const payload = {
        list_id: list.id,
        body,
        sort_order: state.tasks.filter((item) => item.list_id === list.id && !item.deleted_at).length,
      };

      const op = await queueOperation('create_task', 'task', taskId, payload, list.group_id ? 'group' : 'personal', list.group_id || 'personal');
      await applyIncomingOperation({ ...op, actor_user_id: state.user?.id || 0 });
      await dispatch('relayOptimisticOperation', { ...op, actor_user_id: state.user?.id || 0 });
      await dispatch('hydrateLocal');
      if (state.mode === 'user') {
        await dispatch('syncNow');
      }
      await dispatch('broadcastLocalRefresh');
      return taskId;
    },

    async attachImageToTask({ state, commit, dispatch }, { taskId, file }) {
      if (state.mode !== 'user') {
        commit('setAuthModalOpen', true);
        throw new Error('Debes iniciar sesión para guardar imágenes.');
      }
      if (!state.online) {
        throw new Error('Para subir imágenes necesitas conexión.');
      }
      if (!file) {
        throw new Error('No se encontró el archivo a subir.');
      }

      const formData = new FormData();
      formData.append('image', file);
      const response = await api.post(`/tasks/${taskId}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await applyTaskLocal(response.data?.data);
      await dispatch('hydrateLocal');
      await dispatch('broadcastLocalRefresh');
      return response.data?.data;
    },

    async removeTaskImage({ state, commit, dispatch }, taskId) {
      if (state.mode !== 'user') {
        commit('setAuthModalOpen', true);
        throw new Error('Debes iniciar sesión para administrar imágenes.');
      }
      if (!state.online) {
        throw new Error('Para quitar imágenes necesitas conexión.');
      }

      const response = await api.delete(`/tasks/${taskId}/image`);
      await applyTaskLocal(response.data?.data);
      await dispatch('hydrateLocal');
      await dispatch('broadcastLocalRefresh');
      return response.data?.data;
    },

    async toggleTask({ state, dispatch }, taskId) {
      const task = state.tasks.find((item) => item.id === taskId);
      if (!task) return;
      const list = state.lists.find((item) => item.id === task.list_id);
      if (!list) return;

      const op = await queueOperation('set_task_completed', 'task', taskId, { completed: !isTaskCompleted(task) }, list.group_id ? 'group' : 'personal', list.group_id || 'personal');
      await applyIncomingOperation({ ...op, actor_user_id: state.user?.id || 0 });
      await dispatch('relayOptimisticOperation', { ...op, actor_user_id: state.user?.id || 0 });
      await dispatch('hydrateLocal');
      if (state.mode === 'user') {
        await dispatch('syncNow');
      }
      await dispatch('broadcastLocalRefresh');
    },

    async renameTask({ state, dispatch }, { taskId, body }) {
      const task = state.tasks.find((item) => item.id === taskId);
      if (!task) return;
      const list = state.lists.find((item) => item.id === task.list_id);
      if (!list) return;

      const op = await queueOperation('update_task_text', 'task', taskId, { body, sort_order: task.sort_order }, list.group_id ? 'group' : 'personal', list.group_id || 'personal');
      await applyIncomingOperation({ ...op, actor_user_id: state.user?.id || 0 });
      await dispatch('relayOptimisticOperation', { ...op, actor_user_id: state.user?.id || 0 });
      await dispatch('hydrateLocal');
      await dispatch('syncNow');
      await dispatch('broadcastLocalRefresh');
    },

    async reorderTasks({ state, dispatch }, { listId, orderedTaskIds }) {
      const list = state.lists.find((item) => item.id === listId && !item.deleted_at);
      if (!list || !Array.isArray(orderedTaskIds) || orderedTaskIds.length < 2) return;

      const tasksById = new Map(
        state.tasks
          .filter((item) => item.list_id === listId && !item.deleted_at)
          .map((item) => [item.id, item])
      );

      const validIds = orderedTaskIds.filter((taskId) => tasksById.has(taskId));
      if (validIds.length < 2) return;

      const currentOrder = [...tasksById.values()]
        .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0))
        .map((item) => item.id);

      if (JSON.stringify(currentOrder) === JSON.stringify(validIds)) return;

      const scopeType = list.group_id ? 'group' : 'personal';
      const scopeId = list.group_id || 'personal';

      for (let index = 0; index < validIds.length; index += 1) {
        const taskId = validIds[index];
        const task = tasksById.get(taskId);
        if (!task) continue;
        const nextSortOrder = index;
        if (Number(task.sort_order || 0) === nextSortOrder) continue;

        const op = await queueOperation('update_task_text', 'task', taskId, {
          body: task.body,
          sort_order: nextSortOrder,
        }, scopeType, scopeId);

        await applyIncomingOperation({ ...op, actor_user_id: state.user?.id || 0 });
        await dispatch('relayOptimisticOperation', { ...op, actor_user_id: state.user?.id || 0 });
      }

      await dispatch('hydrateLocal');
      await dispatch('syncNow');
      await dispatch('broadcastLocalRefresh');
    },

    async deleteTask({ state, dispatch }, taskId) {
      const task = state.tasks.find((item) => item.id === taskId);
      if (!task) return;
      const list = state.lists.find((item) => item.id === task.list_id);
      if (!list) return;

      const op = await queueOperation('delete_task', 'task', taskId, {}, list.group_id ? 'group' : 'personal', list.group_id || 'personal');
      await removeTaskLocal(taskId, op.client_ts);
      await dispatch('relayOptimisticOperation', { ...op, actor_user_id: state.user?.id || 0 });
      await dispatch('hydrateLocal');
      if (state.mode === 'user') {
        await dispatch('syncNow');
      }
      await dispatch('broadcastLocalRefresh');
    },

    async broadcastLocalRefresh() {
      publishLocalChange({ type: 'local-refresh' });
    },

    async loadBillingStatus({ state, commit }) {
      if (state.mode !== 'user') {
        commit('setBilling', { ...buildDefaultBilling(), loaded: true });
        return;
      }
      try {
        const response = await api.get('/billing/status');
        commit('setBilling', { ...(response.data?.data || buildDefaultBilling()), loaded: true });
      } catch (error) {
        console.warn(error);
        commit('setBilling', { ...buildDefaultBilling(), loaded: true });
      }
    },

    async startCheckout({ state, commit, dispatch }, plan = 'yearly') {
      if (state.mode !== 'user') {
        commit('setAuthModalOpen', true);
        return;
      }
      const planCode = typeof plan === 'string' ? plan : (plan?.code || plan?.key || plan?.id || 'yearly');
      const response = await api.post('/billing/checkout-session', { plan: planCode });
      const url = response.data?.data?.url;
      if (url) {
        window.location.href = url;
        return;
      }
      await dispatch('loadBillingStatus');
    },

    async openBillingPortal({ state, commit }) {
      if (state.mode !== 'user') {
        commit('setAuthModalOpen', true);
        return;
      }
      const response = await api.post('/billing/portal-session');
      const url = response.data?.data?.url;
      if (url) {
        window.location.href = url;
      }
    },

    async createGroup({ dispatch }, payload) {
      await api.post('/groups', payload);
      await dispatch('bootstrap');
    },

    async inviteToGroup({ dispatch }, payload) {
      const response = await api.post(`/groups/${payload.groupId}/invites`, payload.data);
      await dispatch('bootstrap');
      return response.data?.data;
    },

    async acceptInviteIfNeeded() {
      return null;
    },

    async tryAcceptPendingInvite() {
      return null;
    },
  },
});

db.open().catch(() => null);

export default store;
